import classNames from 'classnames'
import React, {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ChatContext } from '~/service/chatConn'
import scrollIntoView from 'scroll-into-view-if-needed'
import useOnclickOutside from 'react-cool-onclickoutside'
import { useHotkeys } from 'react-hotkeys-hook'
import { throttle } from 'lodash'
import { AutocompleteItem } from '~/service/chatConn/types'
import MiniSearch from 'minisearch'
import { getKnowledgebaseTopics } from '~/service/chatConn/init'
import { FormType } from '.'
import { UseFormSetValue } from 'react-hook-form'

type Props = {
  content: string
  redirectToUser: number | undefined
  inputRef: React.MutableRefObject<HTMLTextAreaElement | undefined>
  wrapperRef: React.RefObject<HTMLDivElement>
  autocompleteActiveRef: React.MutableRefObject<string | undefined>
  setValue: UseFormSetValue<FormType>
}

const Autocomplete: FC<Props> = ({
  redirectToUser,
  content,
  inputRef,
  wrapperRef,
  autocompleteActiveRef: activeRef,
  setValue,
}) => {
  const { cannedResponses, stories, autocompleteSearch, activeChat, channel } =
    useContext(ChatContext)

  const [autocompleteItems, setAutocompleteItems] = useState<
    AutocompleteItem[]
  >([])

  const contentRef = useRef(content)
  contentRef.current = content

  const redirectToUserRef = useRef(redirectToUser)
  redirectToUserRef.current = redirectToUser

  const activeChatRef = useRef(activeChat)
  activeChatRef.current = activeChat

  const ref = useOnclickOutside(e => {
    if (e.target instanceof Element) {
      if (wrapperRef.current?.contains(e.target)) {
        return
      }
    }

    if (contentRef.current == '/') {
      setValue('content', '')
    }
  })

  useHotkeys('/', e => {
    if (!contentRef.current.length && activeChatRef.current.active) {
      e.preventDefault()

      setValue('content', '/')

      setTimeout(() => {
        inputRef.current?.focus()
      })
    }
  })

  const autocompleteItemsRef = useRef(autocompleteItems)
  autocompleteItemsRef.current = autocompleteItems

  const [active, setActive] = useState<string>()

  activeRef.current = active

  const isSlash = content.startsWith('/')
  const isSlashRef = useRef(false)
  isSlashRef.current = isSlash

  const [isChosen, setChosen] = useState(false)

  const throttleCounterRef = useRef(0)

  useEffect(() => {
    if (active) {
      const div = document.getElementById('autocomplete' + active)

      if (div) {
        scrollIntoView(div, { scrollMode: 'if-needed' })
      }
    }
  }, [active])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.addEventListener('keydown', e => {
        const up = e.key == 'ArrowUp'
        const down = e.key == 'ArrowDown'
        const enter = e.key == 'Enter'
        const esc = e.key == 'Escape'

        if (esc) {
          e.preventDefault()

          setChosen(true)
          setActive(undefined)

          return
        }

        const items = autocompleteItemsRef.current

        if (items.length) {
          if (up || down) {
            e.preventDefault()
          }

          const currentIndex = items.findIndex(
            item => item.id == activeRef.current
          )

          if (currentIndex == -1) {
            if (up) {
              setActive(items[items.length - 1].id)
            } else if (down) {
              setActive(items[0].id)
            }
          } else {
            if (up) {
              setActive(!currentIndex ? undefined : items[currentIndex - 1].id)
            } else if (down) {
              setActive(
                items[currentIndex + 1] ? items[currentIndex + 1].id : undefined
              )
            } else if (enter) {
              e.preventDefault()
              setChosen(true)

              const item = items[currentIndex]

              setTimeout(() => {
                if (item.type == 'kbTopic' || item.type == 'cannedResponse') {
                  const response = item.message
                  setValue('content', response)
                } else if (item.type == 'story') {
                  setValue('redirectToStory', item.originId)
                  setValue('content', '')
                }

                setActive(undefined)
              })
            }
          }
        }
      })
    }
  }, [])

  const searchThrottled = useMemo(
    () =>
      throttle((search: string) => {
        ;(async () => {
          throttleCounterRef.current++

          if (!search.length) {
            const defaultStories = redirectToUserRef.current ? [] : stories

            setAutocompleteItems(
              isSlashRef.current ? [...cannedResponses, ...defaultStories] : []
            )

            return
          }

          if (!isSlashRef.current) {
            return
          }

          const miniSearchItems = searchInMiniSearch(
            autocompleteSearch,
            search,
            !!redirectToUserRef.current
          )

          setAutocompleteItems(miniSearchItems)

          const throttleCounter = throttleCounterRef.current

          const kbTopics = await getKnowledgebaseTopics(
            channel,
            activeChat.system.spaceId ?? -1,
            search
          )

          if (throttleCounter != throttleCounterRef.current) {
            return
          }

          setAutocompleteItems([...miniSearchItems, ...kbTopics])
        })()
      }, 1000),
    []
  )

  useEffect(() => {
    if (content.length < 4) {
      searchThrottled('')
      setChosen(false)
      setActive(undefined)

      return
    }

    if (isChosen) {
      return
    }

    const search = isSlashRef.current ? content.substring(1) : content

    if (!search.length) {
      searchThrottled('')

      return
    }

    searchThrottled(search)
  }, [content])

  useEffect(() => {
    if (isSlash && !autocompleteItems.find(item => item.id == active)) {
      setActive(autocompleteItems[0] ? autocompleteItems[0].id : undefined)
    }
  }, [autocompleteItems.map(item => item.id).join('.')])

  if (isChosen || (!isSlash && !autocompleteItems.length)) {
    return null
  }

  return (
    <div className="chat-input-autocomplete" ref={ref}>
      {!autocompleteItems.length && (
        <div className="chat-input-autocomplete-empty">
          {intl.formatMessage({
            id: 'chat / input / autocomplete / empty',
          })}
        </div>
      )}

      {autocompleteItems.map(item => (
        <div
          className={classNames('chat-input-autocomplete-item', {
            'is-active': active == item.id,
          })}
          key={item.id}
          tabIndex={0}
          id={'autocomplete' + item.id}
          onClick={() => {
            if (item.type == 'kbTopic' || item.type == 'cannedResponse') {
              setValue('content', item.message)
            } else if (item.type == 'story') {
              setValue('redirectToStory', item.originId)
              setValue('content', '')
            }

            setChosen(true)
          }}
        >
          <div className="chat-input-autocomplete-item-name">{item.name}</div>

          {'message' in item && (
            <div className="chat-input-autocomplete-item-message">
              {item.message}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default Autocomplete

const searchInMiniSearch = (
  autocompleteSearch: MiniSearch<AutocompleteItem>,
  search: string,
  redirectToUser: boolean
) => {
  const filteredRaw = autocompleteSearch.search(search, {
    boost: { name: 2 },
    prefix: true,
    fuzzy: 0.3,
  })

  const filteredLow = filteredRaw

  // const filteredLow = filteredRaw.filter(
  //   item => item.score > (isSlashRef.current ? 1 : 15)
  // )

  let cutAt = 1

  for (let k in filteredLow) {
    const index = parseInt(k)

    if (index > 0 && filteredLow[0].score > 3 * filteredLow[index].score) {
      break
    } else {
      cutAt = index + 1
    }
  }

  const filteredLowerThanFirst = filteredLow.slice(0, cutAt)
  const items = filteredLowerThanFirst as unknown as AutocompleteItem[]

  const typeOrder = { cannedResponse: 1, story: 2, kbTopic: 3 }

  return items
    .filter(item => item.type != 'story' || !redirectToUser)
    .sort((a, b) => {
      return typeOrder[a.type] - typeOrder[b.type]
    })
}
